import { useState, type FormEvent } from "react"
import { ArrowRight } from "@untitledui/icons"
import { toast } from "sonner"
import { useAuth } from "@/lib/auth/auth-context"
import { authApi } from "@/lib/api/auth"
import { ApiError } from "@/lib/api/client"
import { useOnboarding } from "@/lib/onboarding/onboarding-context"
import { personalInfoSchema } from "@/lib/onboarding/schemas"
import {
  Field,
  PrimaryButton,
  StaggerField,
  StepActions,
  StepLayout,
  onboardingInputClass,
  onboardingInputDisabledClass,
  onboardingInputErrorClass,
} from "./onboarding-shell"
import { OnboardingContextPanel } from "./context-panel"
import { cx } from "@/lib/utils/cx"

export function PersonalInfoStep() {
  const { user, refreshUser } = useAuth()
  const { goNext, profileDraft, setProfileDraft } = useOnboarding()

  const [firstName, setFirstName] = useState(profileDraft.firstName || user?.firstName || "")
  const [lastName, setLastName] = useState(profileDraft.lastName || user?.lastName || "")
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({})
  const [submitting, setSubmitting] = useState(false)

  const onFirstNameChange = (v: string) => {
    setFirstName(v)
    setProfileDraft({ firstName: v, lastName })
    if (errors.firstName) setErrors((e) => ({ ...e, firstName: undefined }))
  }
  const onLastNameChange = (v: string) => {
    setLastName(v)
    setProfileDraft({ firstName, lastName: v })
    if (errors.lastName) setErrors((e) => ({ ...e, lastName: undefined }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const parsed = personalInfoSchema.safeParse({ firstName, lastName })
    if (!parsed.success) {
      const fieldErrors: typeof errors = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]
        if (key === "firstName" || key === "lastName") fieldErrors[key] = issue.message
      }
      setErrors(fieldErrors)
      return
    }
    setErrors({})

    const dirty =
      parsed.data.firstName !== (user?.firstName ?? "") ||
      parsed.data.lastName !== (user?.lastName ?? "")
    if (!dirty) {
      goNext()
      return
    }

    setSubmitting(true)
    try {
      await authApi.updateProfile({
        firstName: parsed.data.firstName,
        lastName: parsed.data.lastName,
      })
      await refreshUser()
      goNext()
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't save your name"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <StepLayout
      eyebrow="Step 1 — Welcome"
      title="Let's set up your workspace"
      subtitle="A couple of quick details and we'll have you running estimates in under two minutes."
      hero={<OnboardingContextPanel />}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <StaggerField index={0}>
            <Field label="First name" error={errors.firstName} required>
              <input
                value={firstName}
                onChange={(e) => onFirstNameChange(e.target.value)}
                placeholder="Alex"
                maxLength={255}
                className={cx(onboardingInputClass, errors.firstName && onboardingInputErrorClass)}
                autoComplete="given-name"
                autoFocus
              />
            </Field>
          </StaggerField>
          <StaggerField index={1}>
            <Field label="Last name" error={errors.lastName} required>
              <input
                value={lastName}
                onChange={(e) => onLastNameChange(e.target.value)}
                placeholder="Garcia"
                maxLength={255}
                className={cx(onboardingInputClass, errors.lastName && onboardingInputErrorClass)}
                autoComplete="family-name"
              />
            </Field>
          </StaggerField>
        </div>

        <StaggerField index={2}>
          <Field
            label="Email"
            hint="This is the address you signed in with. Contact support to change it."
          >
            <input
              type="email"
              value={user?.email ?? ""}
              readOnly
              disabled
              className={onboardingInputDisabledClass}
            />
          </Field>
        </StaggerField>

        <StaggerField index={3}>
          <StepActions
            primary={
              <PrimaryButton loading={submitting} variant="emerald">
                Continue
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </form>
    </StepLayout>
  )
}
